<template>
  <v-row class="margin-global">
    <v-col cols="12" md="6">
      <v-card elevation="0" class="indicators-card">
        <v-card-text class="pa-6">
          <h3 class="text-center mb-4 section-title">Indicadores da Dotação Orçamentária</h3>
          
          <div class="text-center mb-6">
            <div class="total-value">{{ formatarMoeda(estatisticas.valor_total) }}</div>
            <div class="total-label">Valor Total da Dotação Orçamentária</div>
          </div>

          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="bar-label">Valor Alocado</span>
              <span class="bar-value">{{ formatarMoeda(estatisticas.valor_alocado) }}</span>
            </div>
            <v-progress-linear 
              :value="percentualAlocado" 
              color="#EF4444" 
              height="25" 
              rounded
              class="progress-bar"
            ></v-progress-linear>
          </div>

          <div class="mb-6">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="bar-label">Valor Disponível</span>
              <span class="bar-value">{{ formatarMoeda(estatisticas.valor_restante) }}</span>
            </div>
            <v-progress-linear 
              :value="percentualDisponivel" 
              color="#64748B" 
              height="25" 
              rounded
              class="progress-bar"
            ></v-progress-linear>
          </div>

          <div class="text-center mt-8">
            <div class="d-flex justify-center align-center">
              <div class="chart-container">
                <v-progress-circular 
                  :rotate="-90" 
                  :size="120" 
                  :width="15" 
                  :value="percentualAlocado" 
                  color="#EF4444"
                  class="mr-4"
                >
                  <span class="chart-text">{{ percentualAlocado }}%</span>
                </v-progress-circular>
              </div>
              <div class="chart-container">
                <v-progress-circular 
                  :rotate="-90" 
                  :size="120" 
                  :width="15" 
                  :value="percentualDisponivel" 
                  color="#64748B"
                >
                  <span class="chart-text">{{ percentualDisponivel }}%</span>
                </v-progress-circular>
              </div>
            </div>
            
            <div class="d-flex justify-center align-center mt-4">
              <div class="legend-item mr-6">
                <span class="legend-dot legend-dot-red"></span>
                <span class="legend-text">Valor Alocado</span>
              </div>
              <div class="legend-item">
                <span class="legend-dot legend-dot-blue"></span>
                <span class="legend-text">Valor Disponível</span>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" md="6">
      <v-card elevation="0" class="allocations-card">
        <v-card-text class="pa-6">
          <h3 class="mb-4 section-title">Registros de Alocações</h3>
          
          <v-data-table 
            :headers="headers" 
            :items="registrosPaginados" 
            v-model="itemSelected" 
            hide-default-footer 
            item-key="id"
            class="allocations-table"
          >
            <template v-slot:item.descricao="{ item }">
              <span class="table-text">{{ item.elemento_primario ? item.elemento_primario.descricao : 'N/A' }}</span>
            </template>
            <template v-slot:item.valor="{ item }">
              <span class="table-text table-value">{{ formatarMoeda(item.valor) }}</span>
            </template>
            <template v-slot:item.tipo="{ item }">
              <v-chip 
                :color="getTypeColor(item.tipo_elemento)" 
                class="type-badge" 
                small 
                label
              >
                {{ item.tipo_elemento }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon small @click="removerAlocacao(item)" class="delete-btn">
                <v-icon small color="#6B7280">mdi-delete</v-icon>
              </v-btn>
            </template>

            <template v-slot:no-data>
              <div class="text-center pa-4 no-data-text">
                Nenhum registro encontrado
              </div>
            </template>
          </v-data-table>

          <div class="d-flex justify-center mt-4" v-if="totalRegistros > 0">
            <v-pagination 
              v-model="pagina" 
              :length="totalPaginas"
              color="#16A34A"
              class="custom-pagination"
            ></v-pagination>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { DotacaoService } from "../../services/DotacaoService";

export default {
  name: "DotacaoElementoPrimarioConsolidado",
  components: {},
  data() {
    return {
      estatisticas: {
        valor_total: 0,
        valor_alocado: 0,
        valor_restante: 0,
      },
      itemSelected: [],
      headers: [
        { text: 'Unidade', value: 'descricao', align: 'start' },
        { text: 'Valor', value: 'valor', align: 'end' },
        { text: 'Tipo', value: 'tipo', align: 'center' },
        { text: 'Ações', value: 'actions', sortable: false, align: 'center' }
      ],
      registros: [],
      pagina: 1,
      itemsPorPagina: 10,
      totalRegistros: 0,
    };
  },
  computed: {
    percentualAlocado() {
      if (this.estatisticas !== null && this.estatisticas.valor_total > 0) {
        return Math.round((this.estatisticas.valor_alocado / this.estatisticas.valor_total) * 100);
      }
      return 0;
    },
    percentualDisponivel() {
      if (this.estatisticas !== null && this.estatisticas.valor_total > 0) {
        return Math.round((this.estatisticas.valor_restante / this.estatisticas.valor_total) * 100);
      }
      return 0;
    },
    totalPaginas() {
      return Math.ceil(this.totalRegistros / this.itemsPorPagina);
    },
    registrosPaginados() {
      const inicio = (this.pagina - 1) * this.itemsPorPagina;
      const fim = inicio + this.itemsPorPagina;
      return this.registros.slice(inicio, fim);
    }
  },
  mounted() {
    this.carregarAlocacoes();
    this.carregarEstatisticas();
  },
  methods: {
    async removerAlocacao(item) {
      try {
        const formData = new FormData();
        formData.append("id", item.id);
        const response = await DotacaoService.removerAlocacaoConsolidada(formData);
        if (response && response.data) {
          if (this.registrosPaginados.length === 1 && this.pagina > 1) {
            this.pagina = this.pagina - 1;
          }
          this.carregarAlocacoes();
          this.carregarEstatisticas();
        } else {
          console.error('Não foi possível remover a alocação');
        }
      }
      catch (error) {
        console.error('Ocorreu um erro ao remover a alocação', error);
      }
    },
    async carregarEstatisticas() {
      try {
        const formData = new FormData();
        formData.append("dotacao_id", this.$route.params.id);

        const response = await DotacaoService.getEstatisticasConsolidadas(formData);

        if (response && response.data) {
          this.estatisticas = response.data;
        } else {
          console.error('Não foi possível obter as estatísticas');
        }
      } catch (error) {
        console.error('Ocorreu um erro ao buscar as estatísticas', error);
      }
    },
    async carregarAlocacoes() {
      const formData = new FormData();
      formData.append("dotacao_id", this.$route.params.id);

      DotacaoService.getAlocacoesConsolidadas(formData).then((response) => {
        if (response && response.data && response.data.data) {
          this.registros = response.data.data.map(alocacao => {
            const tipoMap = {
              'App\\Models\\EmendaEstadual': 'EMENDA ESTADUAL',
              'App\\Models\\EmendaFederal': 'EMENDA FEDERAL',
              'App\\Models\\ConvenioFederal': 'CONVÊNIO FEDERAL',
              'App\\Models\\ElementoPrimarioGenerico': 'GENÉRICO',
            };
            
            return {
              ...alocacao,
              tipo_elemento: tipoMap[alocacao.elemento_primario_type] || 'OUTROS'
            };
          });
          this.totalRegistros = this.registros.length;
          if (this.registrosPaginados.length === 0 && this.pagina > 1) {
            this.pagina = 1;
          }
        }
      });
    },
    formatarMoeda(valor) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(valor);
    },
    getTypeColor(type) {
      switch (type) {
        case 'GENÉRICO':
          return '#16A34A';
        case 'EMENDA ESTADUAL':
          return '#3B82F6';
        case 'EMENDA FEDERAL':
          return '#F59E0B';
        case 'CONVÊNIO FEDERAL':
          return '#8B5CF6';
        default:
          return '#6B7280';
      }
    }
  }
};
</script>

<style scoped>
.margin-global {
  margin-top: 20px;
}

/* Cards com fundo cinza */
.indicators-card,
.allocations-card {
  background-color: #F5F5F5 !important;
  border-radius: 12px !important;
}

/* Títulos das seções */
.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
}

/* Valor Total em Destaque */
.total-value {
  font-size: 32px;
  font-weight: bold;
  color: #1F2937;
}

.total-label {
  font-size: 14px;
  color: #6B7280;
  margin-top: 4px;
}

/* Barras de Progresso */
.bar-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.bar-value {
  font-size: 14px;
  font-weight: 600;
  color: #1F2937;
}

.progress-bar {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

/* Gráficos Circulares */
.chart-container {
  position: relative;
}

.chart-text {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
}

/* Legenda */
.legend-item {
  display: flex;
  align-items: center;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
}

.legend-dot-red {
  background-color: #EF4444;
}

.legend-dot-blue {
  background-color: #64748B;
}

.legend-text {
  font-size: 14px;
  color: #374151;
}

/* Tabela de Alocações */
.allocations-table {
  background-color: white !important;
  border-radius: 8px;
}

.allocations-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.table-text {
  color: #374151;
  font-size: 14px;
}

.table-value {
  font-weight: 600;
}

/* Badges de Tipo */
.type-badge {
  font-size: 11px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  color: white !important;
}

/* Botão de Delete */
.delete-btn:hover {
  background-color: rgba(239, 68, 68, 0.1) !important;
}

/* Texto quando não há dados */
.no-data-text {
  color: #6B7280;
  font-size: 14px;
}

/* Paginação */
.custom-pagination >>> .v-pagination__item--active {
  background-color: #16A34A !important;
  color: white !important;
}

.custom-pagination >>> .v-pagination__item {
  background-color: white;
  border: 1px solid #E5E7EB;
}
</style>

